import { Injectable, OnDestroy } from '@angular/core';
import { MpSdk } from 'libs/simlab-matterport/assets/mpSdk';
import {
  Observable,
  Subject,
  debounceTime,
  defer,
  filter,
  first,
  firstValueFrom,
  fromEvent,
  switchMap,
} from 'rxjs';
import { Vector3 } from 'three';
import { MatterportServiceBase } from '../base/matterport-base';
import { Pointer } from '../models/dto';
import { IMatterportEvents } from '../models/events.interface';
import { ViewMode } from '../models/mattertags.interface';
const LONGPRESS_THRESHOLD = 200;

@Injectable()
export class MatterportEventsService
  extends MatterportServiceBase
  implements OnDestroy, IMatterportEvents
{
  private readonly _hold: Subject<Pointer> = new Subject<Pointer>();
  readonly scanClick$ = defer(() => {
    return this.isOpen.pipe(
      filter((isOpen) => isOpen),
      switchMap(() =>
        fromEvent(this.renderer.domElement, 'pointerdown').pipe(
          switchMap((pointerDownEvenet) => {
            return fromEvent(this.renderer.domElement, 'pointerup').pipe(
              filter(
                (pointerUpEvent) =>
                  pointerUpEvent.timeStamp - pointerDownEvenet.timeStamp <
                  LONGPRESS_THRESHOLD
              ),
              debounceTime(100),
              switchMap(() => {
                return this._pointer.pipe(first());
              })
            );
          })
        )
      )
    );
  });
  readonly currentMode$: Observable<ViewMode | null> = defer(() =>
    this.isOpen.pipe(
      filter((isOpen) => isOpen),
      switchMap(
        () =>
          new Observable<ViewMode | null>((subscriber) => {
            const floorSubRef = this.sdk.Mode.current.subscribe(
              (e: MpSdk.Mode.Mode | null) => {
                subscriber.next(e as unknown as ViewMode);
              }
            );
            return function unsubscribe() {
              floorSubRef.cancel();
            };
          })
      )
    )
  );
  readonly scanDetectClick$ = defer(() => {
    return this.isOpen.pipe(
      filter((isOpen) => isOpen),
      switchMap(() =>
        fromEvent(this.renderer.domElement, 'pointerdown').pipe(
          switchMap(() => this._pointer.pipe(first()))
        )
      )
    );
  });

  readonly scanDetectClickPointerUp$ = defer(() => {
    return this.isOpen.pipe(
      filter((isOpen) => isOpen),
      switchMap(() =>
        fromEvent(this.renderer.domElement, 'pointerup').pipe(
          switchMap(() => this._pointer.pipe(first()))
        )
      )
    );
  });

  readonly _pointer: Observable<Pointer> = new Observable<Pointer>(
    (subscriber) => {
      const pointerSubRef = this.sdk.Pointer.intersection.subscribe(
        (intersectionData) => {
          const pointer: Pointer = {
            normal: new Vector3(
              intersectionData.normal.x,
              intersectionData.normal.y,
              intersectionData.normal.z
            ),
            position: new Vector3(
              intersectionData.position.x,
              intersectionData.position.y,
              intersectionData.position.z
            ),
            object: intersectionData.object,
          };
          subscriber.next(pointer);
        }
      );
      return function unsubscribe() {
        pointerSubRef.cancel();
      };
    }
  );
  readonly pointer$ = defer(() =>
    this.isOpen.pipe(
      filter((isOpen) => isOpen),
      switchMap(() => this._pointer)
    )
  );
  readonly scanHold$ = this._hold.asObservable();
  protected _init(): void {}

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }
  fromScanEvent<T>(eventName: string): Observable<T> {
    return this.isOpen.pipe(
      filter((isOpen) => isOpen),
      switchMap(() => {
        return fromEvent<T>(this.renderer.domElement, eventName);
      })
    );
  }
  async emitValue() {
    const pointer = await firstValueFrom(this._pointer);
    this._hold.next(pointer);
  }
}
